import { fail, ok, type Result } from '../result.js';
import { Documento } from './documento.js';

/**
 * Lote 19/10 (frente "países"): documentos e endereço por país. Brasil, Estados Unidos e Espanha têm tipos de documento com
 * máscara, normalização e dígito de controle; os outros países seguem com o "registro no país de origem" em texto livre e o
 * tipo digitado (VAT, RFC, NIPC...). O valor gravado é sempre o normalizado: BR e EUA só dígitos; Espanha letras maiúsculas
 * e dígitos, sem separador; outros países como digitado (bordas cortadas, espaços repetidos viram um).
 */

export type PaisComDocumento = 'BR' | 'US' | 'ES';
export const PAISES_COM_DOCUMENTO: readonly PaisComDocumento[] = ['BR', 'US', 'ES'];
export type NaturezaDocumento = 'juridica' | 'fisica';
export type TipoDocumentoPais = 'cnpj' | 'cpf' | 'ein' | 'ssn' | 'itin' | 'cif' | 'nif' | 'nie';

export const TIPOS_DOCUMENTO_PAIS: Record<PaisComDocumento, Record<NaturezaDocumento, readonly TipoDocumentoPais[]>> = {
  BR: { juridica: ['cnpj'], fisica: ['cpf'] },
  US: { juridica: ['ein'], fisica: ['ssn', 'itin'] },
  ES: { juridica: ['cif'], fisica: ['nif', 'nie'] },
};

/** Sigla de cada tipo (a mesma nos três idiomas; o nome por extenso fica no i18n da tela). */
export const SIGLA_DOCUMENTO: Record<TipoDocumentoPais, string> = { cnpj: 'CNPJ', cpf: 'CPF', ein: 'EIN', ssn: 'SSN', itin: 'ITIN', cif: 'NIF', nif: 'NIF', nie: 'NIE' };

/** Tamanho máximo do tipo digitado nos outros países ("VAT", "RFC", "Company number"). */
export const MAX_TIPO_DOCUMENTO_LIVRE = 30;

const TODOS_OS_TIPOS = new Set<string>(Object.values(TIPOS_DOCUMENTO_PAIS).flatMap((n) => [...n.juridica, ...n.fisica]));

export const ehPaisComDocumento = (pais: string | null | undefined): pais is PaisComDocumento => !!pais && (PAISES_COM_DOCUMENTO as readonly string[]).includes(pais.toUpperCase());
export const ehTipoDocumentoPais = (tipo: string | null | undefined): tipo is TipoDocumentoPais => !!tipo && TODOS_OS_TIPOS.has(tipo);

/** Tipos do país para empresa (`juridica`) ou pessoa (`fisica`); vazio fora de BR, EUA e Espanha (texto livre). */
export function tiposDoPais(pais: string | null | undefined, natureza: NaturezaDocumento): readonly TipoDocumentoPais[] {
  const p = (pais || 'BR').toUpperCase();
  return ehPaisComDocumento(p) ? TIPOS_DOCUMENTO_PAIS[p][natureza] : [];
}

/** País do tipo (CNPJ e CPF no BR, EIN, SSN e ITIN nos EUA, CIF, NIF e NIE na Espanha). */
export function paisDoTipo(tipo: TipoDocumentoPais): PaisComDocumento {
  return (Object.keys(TIPOS_DOCUMENTO_PAIS) as PaisComDocumento[]).find((p) => [...TIPOS_DOCUMENTO_PAIS[p].juridica, ...TIPOS_DOCUMENTO_PAIS[p].fisica].includes(tipo))!;
}

const soDigitos = (v: string) => v.replace(/\D/g, '');
const alfanumerico = (v: string) => v.toUpperCase().replace(/[^0-9A-Z]/g, '');

/** O valor normalizado do tipo, sem conferir (BR e EUA só dígitos; Espanha letras maiúsculas e dígitos). */
export function normalizarDocumentoPais(tipo: TipoDocumentoPais, bruto: string | null | undefined): string {
  const v = String(bruto ?? '');
  return paisDoTipo(tipo) === 'ES' ? alfanumerico(v) : soDigitos(v);
}

// ------------------------------------------------------------------------------------------------- Estados Unidos

/**
 * Prefixos de EIN atribuídos pelo IRS (os dois primeiros dígitos, pelos centros de atendimento e pela internet). Fora da
 * lista: 00, 07, 08, 09, 17, 18, 19, 28, 29, 49, 69, 70, 78, 79, 89, 96 e 97.
 */
const PREFIXOS_EIN = new Set(
  [[1, 6], [10, 16], [20, 27], [30, 48], [50, 68], [71, 77], [80, 88], [90, 95], [98, 99]].flatMap(([a, b]) => Array.from({ length: b! - a! + 1 }, (_, i) => String(a! + i).padStart(2, '0'))),
);
export const prefixoEinValido = (prefixo: string) => PREFIXOS_EIN.has(prefixo);

function einValido(d: string): boolean {
  return /^\d{9}$/.test(d) && PREFIXOS_EIN.has(d.slice(0, 2));
}

/** SSN: AAA-GG-SSSS, sem área 000, 666 ou 900 a 999, sem grupo 00 e sem série 0000. */
function ssnValido(d: string): boolean {
  if (!/^\d{9}$/.test(d)) return false;
  const area = Number(d.slice(0, 3));
  return area !== 0 && area !== 666 && area < 900 && d.slice(3, 5) !== '00' && d.slice(5) !== '0000';
}

/** ITIN: começa com 9 e o grupo (4º e 5º dígitos) fica em 50 a 65, 70 a 88, 90 a 92 ou 94 a 99. */
function itinValido(d: string): boolean {
  if (!/^9\d{8}$/.test(d)) return false;
  const g = Number(d.slice(3, 5));
  return (g >= 50 && g <= 65) || (g >= 70 && g <= 88) || (g >= 90 && g <= 92) || g >= 94;
}

// ------------------------------------------------------------------------------------------------------- Espanha

const LETRAS_DNI = 'TRWAGMYFPDXBNJZSQVHLCKE';
/** Letra de controle do DNI (NIF de pessoa) e do NIE: o número módulo 23. */
export const letraNif = (numero: number | string) => LETRAS_DNI[Number(numero) % 23]!;

/** Letra inicial do CIF (NIF de pessoa jurídica) pela regra da AEAT. */
const LETRAS_CIF = 'ABCDEFGHJNPQRSUVW';
const CIF_SO_LETRA = 'NPQRSW';
const CIF_SO_DIGITO = 'ABEH';
const LETRAS_CONTROLE_CIF = 'JABCDEFGHI';

/** Dígito de controle do CIF: soma dos dígitos pares mais a dos ímpares dobrados (somando os algarismos), complemento a 10. */
export function controleCif(sete: string): { digito: string; letra: string } {
  let soma = 0;
  for (let i = 0; i < 7; i++) {
    const n = Number(sete[i]);
    if (i % 2 === 1) soma += n;
    else {
      const dobro = n * 2;
      soma += Math.floor(dobro / 10) + (dobro % 10);
    }
  }
  const c = (10 - (soma % 10)) % 10;
  return { digito: String(c), letra: LETRAS_CONTROLE_CIF[c]! };
}

function cifValido(v: string): boolean {
  const m = /^([A-Z])(\d{7})([0-9A-J])$/.exec(v);
  if (!m || !LETRAS_CIF.includes(m[1]!)) return false;
  const { digito, letra } = controleCif(m[2]!);
  const c = m[3]!;
  if (CIF_SO_LETRA.includes(m[1]!)) return c === letra;
  if (CIF_SO_DIGITO.includes(m[1]!)) return c === digito;
  return c === digito || c === letra;
}

function nifValido(v: string): boolean {
  const m = /^(\d{8})([A-Z])$/.exec(v);
  return !!m && letraNif(m[1]!) === m[2];
}

function nieValido(v: string): boolean {
  const m = /^([XYZ])(\d{7})([A-Z])$/.exec(v);
  return !!m && letraNif(`${'XYZ'.indexOf(m[1]!)}${m[2]}`) === m[3];
}

// --------------------------------------------------------------------------------------------- validar e formatar

const MENSAGEM: Record<TipoDocumentoPais, string> = {
  cnpj: 'CNPJ inválido',
  cpf: 'CPF inválido',
  ein: 'EIN inválido: 9 dígitos (XX-XXXXXXX) com prefixo atribuído pelo IRS',
  ssn: 'SSN inválido: 9 dígitos (AAA-GG-SSSS), sem 000, 666 ou 9xx no início',
  itin: 'ITIN inválido: 9 dígitos começando com 9 (9XX-XX-XXXX)',
  cif: 'NIF de empresa (CIF) inválido: letra, 7 dígitos e o controle',
  nif: 'NIF inválido: 8 dígitos e a letra de controle',
  nie: 'NIE inválido: X, Y ou Z, 7 dígitos e a letra de controle',
};

/** Código de erro de cada tipo (literal, para o dicionário de erros das três línguas). */
export const CODIGO_INVALIDO: Record<TipoDocumentoPais, string> = {
  cnpj: 'documento.cnpj_invalido',
  cpf: 'documento.cpf_invalido',
  ein: 'documento.ein_invalido',
  ssn: 'documento.ssn_invalido',
  itin: 'documento.itin_invalido',
  cif: 'documento.cif_invalido',
  nif: 'documento.nif_invalido',
  nie: 'documento.nie_invalido',
};

const VALIDA: Record<TipoDocumentoPais, (v: string) => boolean> = {
  cnpj: (v) => {
    const r = Documento.tryCreate(v);
    return r.ok && r.valor.tipo === 'cnpj';
  },
  cpf: (v) => {
    const r = Documento.tryCreate(v);
    return r.ok && r.valor.tipo === 'cpf';
  },
  ein: einValido,
  ssn: ssnValido,
  itin: itinValido,
  cif: cifValido,
  nif: nifValido,
  nie: nieValido,
};

/** Confere o documento do tipo e devolve o valor normalizado; código `documento.<tipo>_invalido` com o campo. */
export function validarDocumentoPais(tipo: TipoDocumentoPais, bruto: string | null | undefined, campo = 'documento'): Result<string> {
  const v = normalizarDocumentoPais(tipo, bruto);
  return VALIDA[tipo](v) ? ok(v) : fail(CODIGO_INVALIDO[tipo], MENSAGEM[tipo], { campo });
}

/** Documento gravado no formato de leitura do tipo (CNPJ 00.000.000/0000-00, EIN 00-0000000, SSN 000-00-0000; Espanha como gravado). */
export function formatarDocumentoPais(tipo: string | null | undefined, valor: string | null | undefined): string {
  const v = String(valor ?? '');
  if (!ehTipoDocumentoPais(tipo)) return v;
  const d = soDigitos(v);
  switch (tipo) {
    case 'cnpj':
      return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : v;
    case 'cpf':
      return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : v;
    case 'ein':
      return d.length === 9 ? `${d.slice(0, 2)}-${d.slice(2)}` : v;
    case 'ssn':
    case 'itin':
      return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : v;
    default:
      return alfanumerico(v);
  }
}

/** Máscara enquanto digita (corta no tamanho do tipo); tipo fora da lista (outros países) volta como veio. */
export function formatarDigitandoDocumentoPais(tipo: string | null | undefined, bruto: string): string {
  if (!ehTipoDocumentoPais(tipo)) return bruto;
  if (paisDoTipo(tipo) === 'ES') return alfanumerico(bruto).slice(0, 9);
  const d = soDigitos(bruto);
  if (tipo === 'ein') {
    const x = d.slice(0, 9);
    return x.length > 2 ? `${x.slice(0, 2)}-${x.slice(2)}` : x;
  }
  if (tipo === 'ssn' || tipo === 'itin') {
    const x = d.slice(0, 9);
    let r = x.slice(0, 3);
    if (x.length > 3) r += `-${x.slice(3, 5)}`;
    if (x.length > 5) r += `-${x.slice(5)}`;
    return r;
  }
  const x = d.slice(0, tipo === 'cpf' ? 11 : 14);
  return formatarParcial(tipo as 'cnpj' | 'cpf', x);
}

function formatarParcial(tipo: 'cnpj' | 'cpf', d: string): string {
  if (tipo === 'cpf') {
    let r = d.slice(0, 3);
    if (d.length > 3) r += `.${d.slice(3, 6)}`;
    if (d.length > 6) r += `.${d.slice(6, 9)}`;
    if (d.length > 9) r += `-${d.slice(9)}`;
    return r;
  }
  let r = d.slice(0, 2);
  if (d.length > 2) r += `.${d.slice(2, 5)}`;
  if (d.length > 5) r += `.${d.slice(5, 8)}`;
  if (d.length > 8) r += `/${d.slice(8, 12)}`;
  if (d.length > 12) r += `-${d.slice(12)}`;
  return r;
}

/** Tamanho completo do tipo (em caracteres normalizados): a tela confere a validade só quando completo. */
export const TAMANHO_DOCUMENTO: Record<TipoDocumentoPais, number> = { cnpj: 14, cpf: 11, ein: 9, ssn: 9, itin: 9, cif: 9, nif: 9, nie: 9 };

/** Exemplo de máscara para o placeholder. */
export const EXEMPLO_DOCUMENTO: Record<TipoDocumentoPais, string> = {
  cnpj: '00.000.000/0000-00',
  cpf: '000.000.000-00',
  ein: '00-0000000',
  ssn: '000-00-0000',
  itin: '9XX-XX-XXXX',
  cif: 'B12345674',
  nif: '12345678Z',
  nie: 'X1234567L',
};

/**
 * Documento de qualquer país com o rótulo do tipo, para relatório, e-mail e documento gerado: "CNPJ 11.444.777/0001-61",
 * "EIN 12-3456789", "NIF B12345674"; outro país, "<tipo digitado> <valor>" (ou só o valor, sem tipo). Sem documento, null.
 */
export function documentoComRotulo(pais: string | null | undefined, tipo: string | null | undefined, valor: string | null | undefined): string | null {
  if (!valor) return null;
  const p = (pais || 'BR').toUpperCase();
  const t = ehTipoDocumentoPais(tipo) ? tipo : p === 'BR' ? (soDigitos(valor).length === 11 ? 'cpf' : 'cnpj') : null;
  if (t) return `${SIGLA_DOCUMENTO[t]} ${formatarDocumentoPais(t, valor)}`;
  return tipo?.trim() ? `${tipo.trim()} ${valor}` : valor;
}

export interface DocumentoNoPais {
  /** Tipo gravado: um de `TipoDocumentoPais` em BR, EUA e Espanha; o digitado (ou null) nos outros países. */
  tipo: string | null;
  valor: string;
}

/**
 * Documento do país (empresa do grupo ou parceiro), validado e normalizado. BR, EUA e Espanha: o tipo precisa ser do país e
 * da natureza (sem tipo, o primeiro do país para empresa; para pessoa só quando o país tem um); outros países: registro em
 * texto livre (`Documento.tryCreateEstrangeiro`) com o tipo digitado, opcional, até 30 caracteres de ASCII visível.
 */
export function documentoDoPais(pais: string | null | undefined, natureza: NaturezaDocumento, tipo: string | null | undefined, bruto: string, campo = 'documento'): Result<DocumentoNoPais> {
  const p = (pais || 'BR').toUpperCase();
  const tipoLimpo = (tipo ?? '').trim().replace(/[ \t]+/g, ' ');
  if (ehPaisComDocumento(p)) {
    const tipos = TIPOS_DOCUMENTO_PAIS[p][natureza];
    const t = tipoLimpo ? tipoLimpo.toLowerCase() : tipos.length === 1 ? tipos[0]! : null;
    if (!t) return fail('documento.tipo_obrigatorio', 'Escolha o tipo do documento', { campo: 'documento_tipo' });
    if (!(tipos as readonly string[]).includes(t)) return fail('documento.tipo_invalido', `Tipo de documento que não é do país (${p})`, { campo: 'documento_tipo', tipos });
    const v = validarDocumentoPais(t as TipoDocumentoPais, bruto, campo);
    return v.ok ? ok({ tipo: t, valor: v.valor }) : v;
  }
  if (tipoLimpo.length > MAX_TIPO_DOCUMENTO_LIVRE) return fail('documento.tipo_longo', `Tipo do documento com até ${MAX_TIPO_DOCUMENTO_LIVRE} caracteres`, { campo: 'documento_tipo' });
  if (tipoLimpo && !/^[\x20-\x7e]+$/.test(tipoLimpo)) return fail('documento.tipo_invalido', 'Use letras sem acento, números e pontuação no tipo do documento', { campo: 'documento_tipo' });
  const d = Documento.tryCreateEstrangeiro(bruto);
  if (!d.ok) return fail(d.erros[0]!.codigo, d.erros[0]!.mensagem, { campo });
  return ok({ tipo: tipoLimpo || null, valor: d.valor.valor });
}

// ------------------------------------------------------------------------------------------------------ endereço

/** Os 50 estados dos EUA e o Distrito de Colúmbia (USPS). */
export const ESTADOS_EUA = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;

/**
 * As 52 províncias da Espanha (com Ceuta e Melilla): o código ISO 3166-2:ES (até 2 letras, o que vai no `uf`), o código do
 * INE (os 2 primeiros dígitos do código postal) e o nome.
 */
export const PROVINCIAS_ES: readonly { codigo: string; ine: string; nome: string }[] = (
  [
    ['VI', '01', 'Álava'], ['AB', '02', 'Albacete'], ['A', '03', 'Alicante'], ['AL', '04', 'Almería'], ['AV', '05', 'Ávila'], ['BA', '06', 'Badajoz'],
    ['PM', '07', 'Illes Balears'], ['B', '08', 'Barcelona'], ['BU', '09', 'Burgos'], ['CC', '10', 'Cáceres'], ['CA', '11', 'Cádiz'], ['CS', '12', 'Castellón'],
    ['CR', '13', 'Ciudad Real'], ['CO', '14', 'Córdoba'], ['C', '15', 'A Coruña'], ['CU', '16', 'Cuenca'], ['GI', '17', 'Girona'], ['GR', '18', 'Granada'],
    ['GU', '19', 'Guadalajara'], ['SS', '20', 'Gipuzkoa'], ['H', '21', 'Huelva'], ['HU', '22', 'Huesca'], ['J', '23', 'Jaén'], ['LE', '24', 'León'],
    ['L', '25', 'Lleida'], ['LO', '26', 'La Rioja'], ['LU', '27', 'Lugo'], ['M', '28', 'Madrid'], ['MA', '29', 'Málaga'], ['MU', '30', 'Murcia'],
    ['NA', '31', 'Navarra'], ['OR', '32', 'Ourense'], ['O', '33', 'Asturias'], ['P', '34', 'Palencia'], ['GC', '35', 'Las Palmas'], ['PO', '36', 'Pontevedra'],
    ['SA', '37', 'Salamanca'], ['TF', '38', 'Santa Cruz de Tenerife'], ['S', '39', 'Cantabria'], ['SG', '40', 'Segovia'], ['SE', '41', 'Sevilla'], ['SO', '42', 'Soria'],
    ['T', '43', 'Tarragona'], ['TE', '44', 'Teruel'], ['TO', '45', 'Toledo'], ['V', '46', 'Valencia'], ['VA', '47', 'Valladolid'], ['BI', '48', 'Bizkaia'],
    ['ZA', '49', 'Zamora'], ['Z', '50', 'Zaragoza'], ['CE', '51', 'Ceuta'], ['ML', '52', 'Melilla'],
  ] as const
).map(([codigo, ine, nome]) => ({ codigo, ine, nome }));

/** Máscara do código postal enquanto digita: CEP 00000-000, ZIP 00000 ou 00000-0000, Espanha 00000; outros, como veio. */
export function mascararCodigoPostal(pais: string | null | undefined, bruto: string): string {
  const p = (pais || 'BR').toUpperCase();
  const d = soDigitos(bruto);
  if (p === 'BR') {
    const x = d.slice(0, 8);
    return x.length > 5 ? `${x.slice(0, 5)}-${x.slice(5)}` : x;
  }
  if (p === 'US') {
    const x = d.slice(0, 9);
    return x.length > 5 ? `${x.slice(0, 5)}-${x.slice(5)}` : x;
  }
  if (p === 'ES') return d.slice(0, 5);
  return bruto;
}

/**
 * Código postal e região (estado ou província) do país. BR: como antes (só o tamanho, a UF com 2 letras); EUA: ZIP de 5 ou 9
 * dígitos e o estado entre os 50 e DC; Espanha: 5 dígitos começando pelo código de uma província e a província, que precisa
 * ser a do código postal; outros países: texto livre (código postal até 12, região até 100). Devolve os dois normalizados.
 */
export function enderecoDoPais(pais: string | null | undefined, cep: string | null, regiao: string | null): Result<{ cep: string | null; uf: string | null }> {
  const p = (pais || 'BR').toUpperCase();
  const r = regiao?.trim() || null;
  const c = cep?.trim() || null;
  if (p === 'BR') {
    if (r && r.length > 2) return fail('endereco.uf.muito_longo', 'UF com 2 letras', { campo: 'endereco.uf', max: 2 });
    return ok({ cep: c, uf: r ? r.toUpperCase() : null });
  }
  if (p === 'US') {
    let zip: string | null = null;
    if (c) {
      const d = soDigitos(c);
      if (!/^\d{5}(-?\d{4})?$/.test(c.replace(/\s/g, '')) || (d.length !== 5 && d.length !== 9)) return fail('endereco.zip_invalido', 'ZIP code com 5 ou 9 dígitos (00000 ou 00000-0000)', { campo: 'endereco.cep' });
      zip = d.length === 9 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
    }
    const uf = r ? r.toUpperCase() : null;
    if (uf && !(ESTADOS_EUA as readonly string[]).includes(uf)) return fail('endereco.estado_invalido', 'Estado dos EUA pela sigla de 2 letras (os 50 estados e DC)', { campo: 'endereco.uf' });
    return ok({ cep: zip, uf });
  }
  if (p === 'ES') {
    const uf = r ? r.toUpperCase() : null;
    const prov = uf ? PROVINCIAS_ES.find((x) => x.codigo === uf) : undefined;
    if (uf && !prov) return fail('endereco.provincia_invalida', 'Província da Espanha não reconhecida', { campo: 'endereco.uf' });
    if (c) {
      if (!/^\d{5}$/.test(c) || !PROVINCIAS_ES.some((x) => x.ine === c.slice(0, 2))) return fail('endereco.codigo_postal_invalido', 'Código postal da Espanha com 5 dígitos (01000 a 52999)', { campo: 'endereco.cep' });
      if (prov && prov.ine !== c.slice(0, 2)) return fail('endereco.provincia_codigo_postal', `O código postal ${c} não é da província ${prov.nome}`, { campo: 'endereco.cep' });
    }
    return ok({ cep: c, uf });
  }
  if (c && c.length > 12) return fail('endereco.cep.muito_longo', 'Código postal com até 12 caracteres', { campo: 'endereco.cep', max: 12 });
  if (r && r.length > 100) return fail('endereco.uf.muito_longo', 'Estado ou província com até 100 caracteres', { campo: 'endereco.uf', max: 100 });
  return ok({ cep: c, uf: r });
}

/** Moedas da empresa do grupo (lote 19/10): real, dólar e euro. Sem escolha, a do país (EUA dólar, Espanha euro, o resto real). */
export const MOEDAS_EMPRESA = ['BRL', 'USD', 'EUR'] as const;
export type MoedaEmpresa = (typeof MOEDAS_EMPRESA)[number];
export const moedaDoPais = (pais: string | null | undefined): MoedaEmpresa => {
  const p = (pais || 'BR').toUpperCase();
  return p === 'US' ? 'USD' : p === 'ES' ? 'EUR' : 'BRL';
};
